// A flag, not a credential: "the last visit from this browser ended signed in".
//
// The session itself lives in an httpOnly cookie the page cannot read, which is
// the point — but it also means the shell has no way to know, at first paint,
// whether the resume in flight is going to succeed. Without this it renders the
// anonymous shell and then snaps to the signed-in one a round trip later, which
// reads as a flicker on every single load for every signed-in visitor.
//
// So the *fact* of being signed in is mirrored here, where script can read it,
// and nothing else is. It grants nothing: forging it buys an optimistic render
// that the very next response corrects. The cookie remains the only thing that
// can actually resume a session.
//
// localStorage rather than sessionStorage because the point is surviving a
// browser close — the same span the cookie covers. It is cleared on sign-out and
// on any failed resume, so it cannot drift into claiming a session that ended.

const SIGNED_IN_HINT_KEY = 'curri.portal.signedIn'

// Blocked or full storage is never a reason to fail to render, and the hint is
// pure optimisation — every path degrades to the pre-hint behaviour, which is a
// brief anonymous flash rather than a broken app. Same posture as the shell's
// own rail-collapsed preference.

export const readSignedInHint = (): boolean => {
  try {
    return localStorage.getItem(SIGNED_IN_HINT_KEY) === 'true'
  } catch {
    return false
  }
}

export const writeSignedInHint = (): void => {
  try {
    localStorage.setItem(SIGNED_IN_HINT_KEY, 'true')
  } catch {
    // Optimistic rendering is off for this visitor; the app still works.
  }
}

export const clearSignedInHint = (): void => {
  try {
    localStorage.removeItem(SIGNED_IN_HINT_KEY)
  } catch {
    // A hint that cannot be cleared only costs a wrong first paint on the next
    // load, corrected as soon as the resume answers.
  }
}
